import React, { useEffect, useState } from "react";
import { Play, Pause, Volume2, Heart } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { fmtTime } from "@/lib/format";
import { isFav, toggleFav } from "@/lib/favourites";

export const SongRow = ({ song, index, list }) => {
  const { lang, t, fontClass } = useLang();
  const { title: ctitle } = useContentI18n();
  const { current, isPlaying, playIndex, togglePlay } = usePlayer();
  const active = current && current.id === song.id;
  const primaryTitle = ctitle(song);
  const secondaryTitle = lang === "en" ? song.title_si : song.title_en;
  const [fav, setFav] = useState(() => isFav(song.id));
  const [pop, setPop] = useState(false);

  useEffect(() => {
    const sync = () => setFav(isFav(song.id));
    window.addEventListener("sk-favs-changed", sync);
    return () => window.removeEventListener("sk-favs-changed", sync);
  }, [song.id]);

  const onFav = (e) => {
    e.stopPropagation();
    setFav(toggleFav(song.id));
    setPop(true);
    setTimeout(() => setPop(false), 400);
  };

  const onPlay = () => {
    if (active) togglePlay();
    else playIndex(index, list);
  };

  return (
    <div
      data-testid={`song-row-${song.id}`}
      className="flex items-center gap-4 p-3 sm:p-4 rounded-2xl transition-all sk-fade-up"
      style={{
        background: active ? "var(--card-hover)" : "var(--card)",
        border: `2px solid ${active ? "var(--accent-glow)" : "var(--border)"}`,
      }}
    >
      <div className="relative shrink-0">
        <img
          src={song.cover_url}
          alt={song.title_en}
          loading="lazy"
          className="w-16 h-16 sm:w-[76px] sm:h-[76px] rounded-xl object-cover"
          style={{ border: "1px solid var(--border)" }}
        />
        {active && isPlaying && (
          <span className="absolute -bottom-1 -right-1 grid place-items-center w-6 h-6 rounded-full" style={{ background: "var(--accent-glow)", color: "#fff" }}>
            <Volume2 size={14} />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className={`${fontClass()} text-lg sm:text-xl font-bold leading-snug truncate`} style={{ color: "var(--text)" }}>
          {primaryTitle}
        </p>
        <p className={`${fontClass(lang === "en" ? "si" : "en")} text-sm sm:text-base truncate`} style={{ color: "var(--text-muted)" }}>
          {secondaryTitle}
        </p>
        <p className="font-ui text-sm sm:text-base font-medium truncate mt-0.5" style={{ color: "var(--text-2)" }}>
          {song.artist}
        </p>
      </div>

      <span
        className="hidden sm:inline-block font-ui text-sm font-semibold px-3 py-1 rounded-full shrink-0"
        style={{ background: "var(--brass-light)", color: "var(--accent)" }}
      >
        {fmtTime(song.duration_sec)}
      </span>

      <button
        onClick={onFav}
        data-testid={`song-fav-button-${song.id}`}
        aria-label={fav ? t("fav_remove") : t("fav_add")}
        aria-pressed={fav}
        className={`shrink-0 grid place-items-center rounded-full transition-transform active:scale-90 hover:scale-110 ${pop ? "sk-heart-pop" : ""}`}
        style={{ width: 48, height: 48, background: "var(--card)", color: fav ? "#e11d48" : "var(--text-muted)", border: "1px solid var(--border)" }}
      >
        <Heart size={22} fill={fav ? "currentColor" : "none"} />
      </button>

      <button
        onClick={onPlay}
        data-testid={`song-play-button-${song.id}`}
        aria-label={`${active && isPlaying ? "Pause" : "Play"} ${song.title_en}`}
        className="shrink-0 grid place-items-center rounded-full transition-transform active:scale-90 hover:scale-105"
        style={{ width: 56, height: 56, background: active && isPlaying ? "var(--accent)" : "var(--player-bg)", color: "var(--player-accent)" }}
      >
        {active && isPlaying ? <Pause size={26} fill="currentColor" /> : <Play size={26} fill="currentColor" style={{ marginLeft: 2 }} />}
      </button>
    </div>
  );
};
